import React, { useEffect, useState, useCallback } from 'react';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  Table, TableHeader, TableBody, TableRow, TableHead, TableCell,
} from '../components/ui/table';
import { useToast } from '../components/ui/use-toast';
import { todayISO } from '../lib/utils';
import { Plus, X, Save, Loader2 } from 'lucide-react';
import { useRestaurant } from '../context/RestaurantContext';
import { confirm } from '../components/ui/confirm-dialog';
import {
  listTaxLabels, listTaxMigrations, createTaxMigration,
} from '../services/taxRateService';

/**
 * Tax Migration — bulk-swap one tax rate for another across all items, for
 * VAT law changes or product-line restructures. Runs immediately and
 * keeps an audit history.
 */
export default function TaxMigrationPage() {
  const { toast } = useToast();
  const { restaurantId } = useRestaurant();

  const [labels, setLabels] = useState([]);
  const [runs, setRuns] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showForm, setShowForm] = useState(true);
  const [fromId, setFromId] = useState('');
  const [toId, setToId] = useState('');
  const [effective, setEffective] = useState('');
  const [recalc, setRecalc] = useState(true);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    if (!restaurantId) return;
    setLoading(true);
    try {
      const [l, m] = await Promise.all([
        listTaxLabels(restaurantId),
        listTaxMigrations(restaurantId),
      ]);
      setLabels(l);
      setRuns(m);
    } catch {
      toast({ title: 'Error', description: 'Could not load tax migrations', variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  }, [restaurantId, toast]);

  useEffect(() => { load(); }, [load]);

  const create = async () => {
    if (!fromId || !toId || !effective) {
      toast({ title: 'Validation', description: 'From, To and Effective Date are required', variant: 'destructive' });
      return;
    }
    if (fromId === toId) {
      toast({ title: 'Validation', description: 'From and To tax rates must differ', variant: 'destructive' });
      return;
    }
    const fromName = labels.find((l) => String(l.id) === fromId)?.name;
    const toName = labels.find((l) => String(l.id) === toId)?.name;
    const ok = await confirm({
      title: 'Run this tax migration?',
      description: `Every item taxed with "${fromName}" will be switched to "${toName}". This runs immediately.`,
      confirmText: 'Run migration', cancelText: 'Cancel',
    });
    if (!ok) return;
    setSaving(true);
    try {
      const run = await createTaxMigration(restaurantId, {
        from_label_id: Number(fromId),
        to_label_id: Number(toId),
        effective_date: effective,
        scope: 'ALL',
        recalculate_prices: recalc,
      });
      toast({ title: 'Migration completed', description: `${run.items_updated} item(s) updated` });
      setFromId(''); setToId(''); setEffective('');
      load();
    } catch (e) {
      toast({
        title: 'Migration failed',
        description: e?.response?.data?.error || e.message,
        variant: 'destructive',
      });
    } finally {
      setSaving(false);
    }
  };

  const selectCls =
    'w-full h-10 rounded-md border border-gray-200 bg-white px-3 text-sm focus:outline-none focus:border-emerald-500';
  const fmtWhen = (iso) => new Date(iso).toLocaleString([], { dateStyle: 'medium', timeStyle: 'short' });

  return (
    <div className="p-6">
      <div className="flex items-center justify-between mb-1">
        <h1 className="font-display text-3xl font-semibold text-foreground tracking-tight leading-none">Tax Migration</h1>
        {!showForm && (
          <Button onClick={() => setShowForm(true)} className="bg-emerald-600 hover:bg-emerald-700">
            <Plus className="h-4 w-4 mr-1.5" /> New Migration
          </Button>
        )}
      </div>
      <p className="text-sm text-gray-500 mb-5">
        Bulk-swap one tax rate for another across all items — for VAT law changes
        or product-line restructures.
      </p>

      {showForm && (
        <Card className="mb-5">
          <CardContent className="pt-5">
            <div className="flex items-center justify-between mb-4">
              <h2 className="font-semibold">Create · Tax Migration</h2>
              <Button variant="ghost" size="icon" onClick={() => setShowForm(false)}>
                <X className="h-4 w-4" />
              </Button>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label>From Tax Rate <span className="text-red-500">*</span></Label>
                <select className={`${selectCls} mt-1`} value={fromId} onChange={(e) => setFromId(e.target.value)}>
                  <option value="">Select…</option>
                  {labels.map((l) => <option key={l.id} value={l.id}>{l.name}</option>)}
                </select>
              </div>
              <div>
                <Label>To Tax Rate <span className="text-red-500">*</span></Label>
                <select className={`${selectCls} mt-1`} value={toId} onChange={(e) => setToId(e.target.value)}>
                  <option value="">Select…</option>
                  {labels.filter((l) => String(l.id) !== fromId).map((l) => (
                    <option key={l.id} value={l.id}>{l.name}</option>
                  ))}
                </select>
              </div>
              <div>
                <Label>Effective Date <span className="text-red-500">*</span></Label>
                {/* When the new rate starts applying to bills — a date already
                    past would claim to re-price bills that are settled. */}
                <Input className="mt-1" type="date" min={todayISO()} value={effective}
                  onChange={(e) => setEffective(e.target.value)} />
              </div>
              <div>
                <Label>Scope</Label>
                <select className={`${selectCls} mt-1`} value="ALL" disabled>
                  <option value="ALL">All Items</option>
                </select>
              </div>
            </div>
            <label className="inline-flex items-center gap-2 text-sm mt-4">
              <input type="checkbox" checked={recalc} onChange={(e) => setRecalc(e.target.checked)} />
              Recalculate item prices
            </label>
            <div className="flex justify-end gap-2 mt-4 pt-4 border-t">
              <Button variant="outline" onClick={() => setShowForm(false)}>Cancel</Button>
              <Button onClick={create} disabled={saving} className="bg-emerald-600 hover:bg-emerald-700">
                {saving ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <Save className="h-4 w-4 mr-1.5" />}
                Create
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      <Card>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Submitted</TableHead>
              <TableHead>From</TableHead>
              <TableHead>To</TableHead>
              <TableHead>Effective</TableHead>
              <TableHead>Scope</TableHead>
              <TableHead className="text-right">Updated</TableHead>
              <TableHead className="text-right">Failed</TableHead>
              <TableHead>Status</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading ? (
              <TableRow><TableCell colSpan={8} className="text-center py-10 text-gray-400">Loading…</TableCell></TableRow>
            ) : runs.length === 0 ? (
              <TableRow><TableCell colSpan={8} className="text-center py-10 text-gray-400">No records found</TableCell></TableRow>
            ) : runs.map((m) => (
              <TableRow key={m.id}>
                <TableCell className="text-sm">{fmtWhen(m.created_at)}</TableCell>
                <TableCell>{m.from_label_name}</TableCell>
                <TableCell>{m.to_label_name}</TableCell>
                <TableCell>{m.effective_date}</TableCell>
                <TableCell>{m.scope === 'ALL' ? 'All Items' : m.scope}</TableCell>
                <TableCell className="text-right">{m.items_updated}</TableCell>
                <TableCell className="text-right">{m.items_failed}</TableCell>
                <TableCell>
                  <span className={`inline-flex items-center text-[11px] font-semibold px-2.5 py-1 rounded-full ${
                    m.status === 'COMPLETED' ? 'bg-emerald-500 text-white' : 'bg-red-100 text-red-700'
                  }`}>
                    {m.status === 'COMPLETED' ? 'Completed' : 'Failed'}
                  </span>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Card>
    </div>
  );
}
